import {
    ArrowPathIcon,
    CalendarIcon,
    PlusIcon,
    UserIcon,
} from "@heroicons/react/24/outline";
import {
    CalendarDate,
    formattedDateTimeDefault,
    RecurrencePattern,
    TaskParentId,
} from "@sp-crm/core";
import { Icon } from "components/icon";
import { asAdvancedSearchEntityType } from "components/tasks/activity-type-options";
import { DeleteButton, EditButton } from "components/ui/action-button";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { defaultLinkStyle } from "components/ui/link";
import {
    GetUsersForRegionQuery,
    UpdateTaskPayload,
    useDeleteTaskMutation,
    useUpdateTaskMutation,
} from "generated/graphql";
import { useTaskNouns } from "hooks/use-task-nouns";
import React, { useCallback } from "react";
import { Link } from "react-router-dom";
import { getEntityIconName } from "util/entity-icons";
import { handleEvent } from "util/user-events";
import { TaskDoneNote } from "./task-done-note";
import { TaskFormV2 } from "./task-form-v2";
import { TaskInlineCall, TaskInlineText } from "./task-inline-call";
import { Taskv2 } from "./types";

interface TaskLineItemProps {
    task: Taskv2;
    refetch: () => Promise<void>;
    isSelected: boolean;
    onSelected: (task: Taskv2) => void;
    onUnselected: (task: Taskv2) => void;
    modifyLocalTask: (task: Taskv2) => void;
    onAddTask: (source: Taskv2) => void;
    users: GetUsersForRegionQuery["getUsersForRegion"];
}

export const TaskLineItem: React.FC<TaskLineItemProps> = props => {
    const { task, entity } = props.task;
    const nouns = useTaskNouns();
    const {
        refetch,
        isSelected,
        onSelected,
        onUnselected,
        onAddTask,
        modifyLocalTask,
        users,
    } = props;
    const updateTask = useUpdateTaskMutation();
    const deleteTask = useDeleteTaskMutation();

    const doUpdate = useCallback(
        async (_parentId: TaskParentId, params: UpdateTaskPayload) => {
            const updatedTask: Taskv2 = {
                task: { ...task, ...params, done: params.status === "done" },
                entity: entity,
            };
            modifyLocalTask(updatedTask);
            await updateTask.mutateAsync({
                taskId: task.id,
                params,
            });
            await refetch();
        },
        [updateTask, refetch, task, entity, modifyLocalTask],
    );

    const handleToggle = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            if (e.target.checked) {
                handleEvent("task-complete", { source: "task-dashboard" });
            }
            await doUpdate(task.parentId, {
                status: e.target.checked ? "done" : "new",
                completedAt: e.target.checked ? new Date().toISOString() : null,
            });
        },
        [doUpdate, task.parentId],
    );

    const handleDelete = useCallback(async () => {
        const confirm = await fancyConfirm(
            `Delete ${nouns.singular}?`,
            `Are you sure you want to delete "${task.text ?? ""}"?`,
            "Yes, delete",
            "No, cancel",
        );

        if (confirm) {
            await deleteTask.mutateAsync({ taskId: task.id });
            await refetch();
        }
    }, [deleteTask, task.id, task.text, refetch, nouns.singular]);

    const handleSaveNote = useCallback(
        async (notes: string) => {
            handleEvent("task-note-saved", { source: "task-dashboard" });
            modifyLocalTask({ task: { ...task, notes }, entity });
            await updateTask.mutateAsync({ taskId: task.id, params: { notes } });
            await refetch();
        },
        [updateTask, refetch, task, entity, modifyLocalTask],
    );

    const handleAdd = useCallback(
        (source: Taskv2) => {
            onAddTask(source);
        },
        [onAddTask],
    );

    if (isSelected) {
        return (
            <TaskFormV2
                initial={props.task}
                mode="update"
                onClose={() => onUnselected(props.task)}
                saveTask={doUpdate}
            />
        );
    }
    return (
        <TaskLineItemShow
            handleDelete={handleDelete}
            handleToggle={handleToggle}
            handleAdd={handleAdd}
            handleSaveNote={handleSaveNote}
            task={props.task}
            onSelected={onSelected}
            users={users}
        />
    );
};

interface TaskLineItemShowProps {
    task: Taskv2;
    onSelected: (task: Taskv2) => void;
    users: GetUsersForRegionQuery["getUsersForRegion"];
    handleToggle: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleDelete: () => void;
    handleAdd: (source: Taskv2) => void;
    handleSaveNote: (notes: string) => Promise<void>;
}

const TaskLineItemShow: React.FC<TaskLineItemShowProps> = props => {
    const { entity, task } = props.task;
    const nouns = useTaskNouns();
    const { users, handleToggle, onSelected, handleDelete, handleAdd, handleSaveNote } =
        props;
    const matchingUser = users?.find(u => u.id === task.ownerId);

    const dueDate: CalendarDate | null = task.dueDate
        ? CalendarDate.parse(task.dueDate).getOrElse(null)
        : null;

    const handleAddAnotherClicked = React.useCallback(
        async (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            handleAdd(props.task);
        },
        [props.task, handleAdd],
    );

    return (
        <div data-testid="task-line-item">
            <div className="md:space-x-1 lg:space-x-2 flex items-start lg:-mx-2 lg:px-2 py-4 hover:bg-brand-100">
                <div className="space-y-1 md:space-y-0 md:grid md:grid-cols-3 lg:grid-cols-6 md:gap-2 lg:gap-4 flex-1">
                    <div className="flex space-x-2 items-center row-span-2 lg:row-span-1 lg:col-span-2">
                        <div className="w-6 h-6">
                            <input
                                onChange={handleToggle}
                                type="checkbox"
                                checked={task.done}
                                className="w-6 h-6"
                            />
                        </div>
                        <div
                            className={`font-medium
                            ${task.done ? "line-through text-gray-600" : ""}
                        `}>
                            {task.text || <>&nbsp;</>}
                        </div>

                        {task.recurrencePattern &&
                            task.recurrencePattern !== RecurrencePattern.None && (
                                <div
                                    className="flex items-center"
                                    title={`Recurring ${nouns.singular}`}>
                                    <ArrowPathIcon className="w-4 h-4 text-blue-600" />
                                </div>
                            )}

                        {task.taskType ? (
                            <div className="bg-indigo-100 text-indigo-800 inline-block flex-shrink-0 rounded-full px-3 py-0.5 text-sm font-medium">
                                {task.taskType.name}
                            </div>
                        ) : null}
                    </div>
                    <div className="ml-8 md:ml-0">
                        <div className="flex items-center space-x-2">
                            <div className="w-4">
                                <Icon name={getEntityIconName(entity.kind)} />
                            </div>
                            <Link to={`${entity.path}/tasks`}>{entity.displayName}</Link>
                        </div>
                    </div>
                    <div className="ml-8 md:ml-0 space-y-1">
                        <TaskInlineCall
                            contact={entity.contact}
                            entityId={task.parentId}
                            entityType={asAdvancedSearchEntityType(entity.kind)}
                        />
                        <TaskInlineText
                            contact={entity.contact}
                            entityId={task.parentId}
                            entityType={asAdvancedSearchEntityType(entity.kind)}
                        />
                    </div>
                    <div className="ml-8 md:ml-0 text-sm flex items-center space-x-1 text-gray-600">
                        <CalendarIcon className="w-4 h-4" />
                        <span data-testid="task-due-display">
                            {dueDate
                                ? dueDate.humanFriendly()
                                : task.dueDateTime
                                  ? formattedDateTimeDefault(task.dueDateTime)
                                  : "No due date"}
                        </span>
                    </div>
                    {matchingUser?.preferredName ? (
                        <div className="ml-8 md:ml-0 text-sm flex items-center space-x-1 text-gray-600">
                            <UserIcon className="w-4 h-4" />
                            <span>{matchingUser.preferredName}</span>
                        </div>
                    ) : null}
                </div>
                <div className="flex items-start space-x-1">
                    <span data-testid="task-line-edit">
                        <EditButton
                            onClick={() => onSelected(props.task)}
                            backgroundColor="bg-white"
                        />
                    </span>
                    <DeleteButton onClick={handleDelete} backgroundColor="bg-white" />
                </div>
            </div>
            {task.done ? (
                <div className="ml-8 space-y-2">
                    <TaskDoneNote notes={task.notes} onSaveNote={handleSaveNote} />
                    <button
                        onClick={handleAddAnotherClicked}
                        className={defaultLinkStyle}>
                        <div className="flex items-center space-x-1">
                            <PlusIcon className="w-5 h-5" />
                            <span className="text-sm">
                                Add another task for {entity.displayName}
                            </span>
                        </div>
                    </button>
                </div>
            ) : null}
        </div>
    );
};
